/**
 * Javascript tools that belongs to the network category
 */

export { NetworkFile, QueryString, URIComponent }

/**
 * A generic query string handler
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class QueryString {

    /**
     * Encodes the query string with base64
     * @param {string} url - URL, i.e., player.html?media=https://storage.googleapis.com/shaka-demo-assets/angel-one/dash.mpd
     * @return {string} Encoded url, i.e., player.html?bWVkaWE9aHR0cHM6Ly9zdG9yYWdlLmdvb2dsZWFwaXMuY29tL3NoYWthLWRlbW8tYXNzZXRzL2FuZ2VsLW9uZS9kYXNoLm1wZA==
     */
    static encode(url) {
        return url.substring(0, url.indexOf('?') + 1) + btoa(url.substring(url.indexOf('?') + 1))
    }

    /**
     * Decodes the query string with base64
     * @param {string} url - URL, i.e., player.html?bWVkaWE9aHR0cHM6Ly9zdG9yYWdlLmdvb2dsZWFwaXMuY29tL3NoYWthLWRlbW8tYXNzZXRzL2FuZ2VsLW9uZS9kYXNoLm1wZA==
     * @return {string} Decoded url, i.e., player.html?media=https://storage.googleapis.com/shaka-demo-assets/angel-one/dash.mpd
     */
    static decode(url) {
        const startPositionQueryString = url.indexOf('?') + 1;
        var queryString = url.substring(startPositionQueryString);
        try {
            queryString = atob(queryString);
        }
        catch (e) {
        }
        return url.substring(0, startPositionQueryString) + queryString
    }

    /**
     * Get the query string parameter keys and values in a url. If the query string is base64 encoded, decoding is applied automatically
     * @param {string} url - URL containing parameters in query string
     * @param {string|Array} [separateValuesKeys] - List of keys where values should be split based on regex expression
     * @param {RegExp} [separateValuesRegex] - Regex expression to split joined query string values into separate values
     * @return {Object.<string, Array>} Dictionary with the input parameter(s) as key(s) and the result(s) as key item(s) in an array
     */
    static getParameter(url, separateValuesKeys = [], separateValuesRegex = /[]/) {

        function _applyValueSplitting(item, expr) {
            var collect = []
            if (typeof item == 'object' && Array.isArray(item)) {
                collect = item.map(item => item.split(expr))
                collect = collect.flat(1);
            }
            else if (typeof item == 'string') {
                collect = item.split(expr)
            }
            return collect
        }

        let parameterDict = {}
        const urlObject = new URL(QueryString.decode(url))
        const params = new URLSearchParams(urlObject.search);
        for (var value of params.keys()) {
            parameterDict[value] = params.getAll(value);
        }

        separateValuesKeys.forEach(queryKey => {
            parameterDict[queryKey] = _applyValueSplitting(urlObject.searchParams.getAll(queryKey), separateValuesRegex)
        })
        return parameterDict

    }
}

/**
 * Class related to network file management
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class NetworkFile {

    /**
     * Asynchronous method to download a file, i.e., a WebVTT file. If its status is 200, the file content is handed over to a callback function
     * @param {string} url - file path as url
     * @param {string} mimeType - MIME type, i.e., 'text/vtt', 'image/jpeg'
     * @param {callback} [callback] - callback that should be executed after the download, containing the content of the downloaded file as text as input argument
     */
    static getHttpFile(url, mimeType, callback) {
        const asynchronousRequest = true;

        var xmlHttp = new XMLHttpRequest();
        xmlHttp.overrideMimeType(mimeType);
        xmlHttp.onreadystatechange = function () {
            if (xmlHttp.readyState == 4 && xmlHttp.status == 200)
                if (typeof callback === 'function' && callback)
                    callback(xmlHttp.responseText);
        }

        xmlHttp.open("GET", url, asynchronousRequest);
        xmlHttp.send(null);
    }

    /**
     * Check if a file is reachable on a server via asynchronous header request. Returns true if file status is not 4xx, i.e., 404 (file not found)
     * @private
     * @param {string} url - file path as url
     * @param {callback} callback - function that should be executed afterwards
     */
    static isReachableHttpFile_(url, callback) {
        var xmlHttp = new XMLHttpRequest();
        xmlHttp.overrideMimeType("text/vtt");
        xmlHttp.onreadystatechange = function () {
            if (xmlHttp.readyState == 4 && xmlHttp.status.toString()[0] != "4")
                callback();
        }
        xmlHttp.open('HEAD', url, true);
        xmlHttp.send(null);
    }
}

/**
 * Class related to uri components
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class URIComponent {

    /**
     * Safely decode URI components where percent characters ("%") are used, that do not indicate escape characters.
     * Examples:
     * 1) "?filter=event+like+'MSM95_52%25'" -> "?filter=event+like+'MSM95_52%'"
     * 2) "?filter=event+like+'MSM95_52%'" (Note: malformed, "%" is not valid) -> "?filter=event+like+'MSM95_52%'"
     * 
     * Inspired by: https://stackoverflow.com/a/54310080
     * 
     * @param {string} encodedURI - encoded URI
     * @return {string} decoded URI
     */
    static decodeSafe(encodedURI) {
        // replaces every % NOT followed by a two-digit (hex) number with %25
        const regex = /%(?![0-9a-fA-F]{2})/g;
        return (!encodedURI) ? encodedURI : decodeURIComponent(encodedURI.replace(regex, '%25'))
    }

    /**
     * The percent character % indicates the start of an escape character.
     * To use it as a regular character, it has to be encoded as %25 in URLs.
     * When using decodeURIComponent() in JS with a string containing %25, it returns % (that is technically correct).
     * However when using this string as an URL (i.e., via history.replaceState()), the URL got broken.
     * This function replaces regular percent characters that are not used as a starting sequence of an escape character.
     * The returned string is a safe decoded URI component that can be used in URLs.
     * 
     * Examples:
     * 1) "?filter=event+like+%27MSM95_52%25%27" -> "filter=event+like+'MSM95_52%25'"
     * 2) "?filter=event+like+'MSM95_52%25'" -> "filter=event+like+'MSM95_52%25'"
     * 3) "?filter=event+like+'MSM95_52%'" (Note: malformed, "%" is not valid) -> "?filter=event+like+'MSM95_52%'"
     * 
     * @param {string} encodedURI - encoded URI
     * @return {string} decoded URI
     */
    static decodeSafeForHistory(encodedURI) {
        // replaces every % NOT followed by a two-digit (hex) number with %25
        const regex = /%(?![0-9a-fA-F]{2})/g;
        return (!encodedURI) ? encodedURI : URIComponent.decodeSafe(encodedURI).replace(regex, '%25');
    }
}